/** Self-service user account & security page (/account): profile, password, and 2FA. */
import { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  activateTotp,
  AuthError,
  changePassword,
  deactivateTotp,
  getOrGenerateRecoveryCodes,
  getTotpSetup,
  type TotpSetupMeta,
} from "@/api/auth";
import { api } from "@/api/client";
import { ApiError, unwrap } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import { PageHeader } from "@/shared/components/page";
import { Button } from "@/shared/ui/button";
import { Field, Input, Select } from "@/shared/ui/form";
import { Badge, Card } from "@/shared/ui/primitives";

/** Dedicated user account and security management page. */
export function AccountPage(): ReactElement {
  const { t, i18n } = useTranslation();
  const { me, refreshMe } = useAuth();

  // Profile state
  const [displayName, setDisplayName] = useState(me?.display_name ?? "");
  const [phone, setPhone] = useState(me?.phone ?? "");
  const [locale, setLocale] = useState(me?.locale ?? "en");
  const [timezone, setTimezone] = useState(me?.timezone ?? "");
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileNotice, setProfileNotice] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);

  // Password state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordNotice, setPasswordNotice] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // 2FA state
  const [totpSetup, setTotpSetup] = useState<TotpSetupMeta | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);

  useEffect(() => {
    if (me) {
      setDisplayName(me.display_name);
      setPhone(me.phone ?? "");
      setLocale(me.locale);
      setTimezone(me.timezone ?? "");
    }
  }, [me]);

  const saveProfile = async (e: { preventDefault(): void }): Promise<void> => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileNotice(null);
    setProfileError(null);
    try {
      const normalizedLocale: "en" | "ar" | "fr" =
        locale === "ar" || locale === "fr" ? locale : "en";
      unwrap(
        await api.PATCH("/api/v1/me", {
          body: {
            display_name: displayName.trim(),
            phone: phone.trim(),
            locale: normalizedLocale,
            timezone: timezone.trim(),
          },
        }),
      );
      if (i18n.resolvedLanguage !== normalizedLocale) {
        await i18n.changeLanguage(normalizedLocale);
      }
      await refreshMe();
      setProfileNotice(t("account.profileSaved"));
    } catch (err) {
      setProfileError(err instanceof ApiError ? err.message : t("app.error"));
    } finally {
      setProfileSaving(false);
    }
  };

  const savePassword = async (e: { preventDefault(): void }): Promise<void> => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setPasswordError(t("auth.passwordsDoNotMatch"));
      return;
    }
    setPasswordSaving(true);
    setPasswordNotice(null);
    setPasswordError(null);
    try {
      await changePassword(me?.has_usable_password ? currentPassword : undefined, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      await refreshMe();
      setPasswordNotice(t("account.passwordUpdated"));
    } catch (err) {
      setPasswordError(err instanceof AuthError ? err.message : t("app.error"));
    } finally {
      setPasswordSaving(false);
    }
  };

  const startTotpEnrollment = async (): Promise<void> => {
    setMfaBusy(true);
    setMfaError(null);
    try {
      const result = await getTotpSetup();
      if (!result.active) {
        setTotpSetup(result.setup);
        setTotpCode("");
      }
    } catch (err) {
      setMfaError(err instanceof AuthError ? err.message : t("app.error"));
    } finally {
      setMfaBusy(false);
    }
  };

  const confirmTotpEnrollment = async (e: { preventDefault(): void }): Promise<void> => {
    e.preventDefault();
    setMfaBusy(true);
    setMfaError(null);
    try {
      await activateTotp(totpCode);
      setTotpSetup(null);
      setTotpCode("");
      const rc = await getOrGenerateRecoveryCodes(false);
      if (rc?.unused_codes?.length) {
        setRecoveryCodes(rc.unused_codes);
      }
      await refreshMe();
    } catch (err) {
      setMfaError(err instanceof AuthError ? err.message : t("app.error"));
    } finally {
      setMfaBusy(false);
    }
  };

  const handleDisableTotp = async (): Promise<void> => {
    setMfaBusy(true);
    setMfaError(null);
    try {
      await deactivateTotp();
      setRecoveryCodes(null);
      setTotpSetup(null);
      await refreshMe();
    } catch (err) {
      setMfaError(err instanceof AuthError ? err.message : t("app.error"));
    } finally {
      setMfaBusy(false);
    }
  };

  const handleRegenerateCodes = async (): Promise<void> => {
    setMfaBusy(true);
    setMfaError(null);
    try {
      const rc = await getOrGenerateRecoveryCodes(true);
      setRecoveryCodes(rc?.unused_codes ?? []);
    } catch (err) {
      setMfaError(err instanceof AuthError ? err.message : t("app.error"));
    } finally {
      setMfaBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <PageHeader title={t("account.title")} subtitle={t("account.subtitle")} />

      <Card className="space-y-4 p-4">
        <h2 className="text-sm font-semibold">{t("account.profileSection")}</h2>
        <form onSubmit={(e) => void saveProfile(e)} className="space-y-3" noValidate>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field id="account-email" label={t("auth.email")}>
              <Input id="account-email" type="email" value={me?.email ?? ""} readOnly />
            </Field>
            <Field id="account-display-name" label={t("app.name")}>
              <Input
                id="account-display-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </Field>
            <Field id="account-phone" label={t("account.phone")}>
              <Input id="account-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </Field>
            <Field id="account-locale" label={t("app.language")}>
              <Select
                id="account-locale"
                value={locale}
                onChange={(e) => setLocale(e.target.value)}
              >
                {SUPPORTED_LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field id="account-timezone" label={t("account.timezone")}>
              <Input
                id="account-timezone"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                placeholder="UTC"
              />
            </Field>
          </div>
          {me?.sso_providers?.length ? (
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>{t("account.linkedSso")}:</span>
              {me.sso_providers.map((p) => (
                <Badge key={p} tone="info">
                  {p}
                </Badge>
              ))}
            </div>
          ) : null}
          {profileError ? (
            <p role="alert" className="text-xs text-destructive">
              {profileError}
            </p>
          ) : null}
          {profileNotice ? <p className="text-xs text-brand-foreground">{profileNotice}</p> : null}
          <div className="flex justify-end">
            <Button type="submit" disabled={profileSaving}>
              {profileSaving ? t("app.saving") : t("app.save")}
            </Button>
          </div>
        </form>
      </Card>

      <Card className="space-y-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold">{t("account.passwordSection")}</h2>
            <p className="text-xs text-muted-foreground">{t("auth.passwordPolicyHint")}</p>
          </div>
          {!me?.has_usable_password ? (
            <Badge tone="neutral">{t("account.ssoManagedPassword")}</Badge>
          ) : null}
        </div>
        <form onSubmit={(e) => void savePassword(e)} className="space-y-3" noValidate>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {me?.has_usable_password ? (
              <Field id="account-current-password" label={t("account.currentPassword")}>
                <Input
                  id="account-current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </Field>
            ) : null}
            <Field id="account-new-password" label={t("auth.newPassword")}>
              <Input
                id="account-new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </Field>
            <Field id="account-confirm-password" label={t("auth.confirmPassword")}>
              <Input
                id="account-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </Field>
          </div>
          {passwordError ? (
            <p role="alert" className="text-xs text-destructive">
              {passwordError}
            </p>
          ) : null}
          {passwordNotice ? (
            <p className="text-xs text-brand-foreground">{passwordNotice}</p>
          ) : null}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={
                passwordSaving ||
                !newPassword ||
                !confirmPassword ||
                (Boolean(me?.has_usable_password) && !currentPassword)
              }
            >
              {passwordSaving ? t("app.saving") : t("account.updatePassword")}
            </Button>
          </div>
        </form>
      </Card>

      <Card className="space-y-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold">{t("account.mfaSection")}</h2>
            <p className="text-xs text-muted-foreground">{t("account.mfaDescription")}</p>
          </div>
          <Badge tone={me?.mfa_enabled ? "success" : "neutral"}>
            {me?.mfa_enabled ? t("account.mfaEnabled") : t("account.mfaDisabled")}
          </Badge>
        </div>

        {mfaError ? (
          <p role="alert" className="text-xs text-destructive">
            {mfaError}
          </p>
        ) : null}

        {!me?.mfa_enabled ? (
          !totpSetup ? (
            <div>
              <Button
                type="button"
                variant="outline"
                onClick={() => void startTotpEnrollment()}
                disabled={mfaBusy}
              >
                {t("account.enableMfa")}
              </Button>
            </div>
          ) : (
            <form
              onSubmit={(e) => void confirmTotpEnrollment(e)}
              className="space-y-3 rounded-md border bg-muted/30 p-3"
              noValidate
            >
              <p className="text-xs text-foreground">{t("account.totpSetupInstructions")}</p>
              <Field id="totp-secret" label={t("account.totpSecretKey")}>
                <Input id="totp-secret" value={totpSetup.secret} readOnly className="font-mono" />
              </Field>
              <Field id="totp-verify-code" label={t("auth.mfaCode")}>
                <Input
                  id="totp-verify-code"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                />
              </Field>
              <div className="flex flex-wrap gap-2">
                <Button type="submit" disabled={mfaBusy || !totpCode.trim()}>
                  {t("account.verifyAndActivate")}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setTotpSetup(null)}
                  disabled={mfaBusy}
                >
                  {t("app.cancel")}
                </Button>
              </div>
            </form>
          )
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleRegenerateCodes()}
              disabled={mfaBusy}
            >
              {t("account.regenerateRecoveryCodes")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void handleDisableTotp()}
              disabled={mfaBusy}
            >
              {t("account.disableMfa")}
            </Button>
          </div>
        )}

        {recoveryCodes && recoveryCodes.length > 0 ? (
          <div className="space-y-2 rounded-md border bg-muted/30 p-3">
            <p className="text-xs font-semibold text-foreground">
              {t("account.recoveryCodesTitle")}
            </p>
            <p className="text-xs text-muted-foreground">{t("account.recoveryCodesHint")}</p>
            <div className="grid grid-cols-2 gap-1.5 font-mono text-xs sm:grid-cols-4">
              {recoveryCodes.map((code) => (
                <div key={code} className="rounded border bg-card px-2 py-1 text-center">
                  {code}
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
